/**
 * PortalActivatePage — Activación de cuenta del colaborador (Caso A).
 *
 * Rutas: /colaboradores/activate/:token (el correo de invitación) y
 *        /colaboradores/restablecer/:token (recuperar la contraseña).
 *
 * Las dos crean una contraseña con un token de un solo uso y usan el mismo
 * endpoint; solo cambian los textos.
 */
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { usePortalActivate } from '../hooks/useEmployeePortal'
import { usePortalAuthStore } from '@/store/portalAuthStore'
import { extractErrorMessage } from '@/lib/errorUtils'

export default function PortalActivatePage({ modo = 'activar' }: { modo?: 'activar' | 'restablecer' }) {
  const restablecer = modo === 'restablecer'
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { setTokens } = usePortalAuthStore()
  const activateMutation = usePortalActivate()

  const [newPassword, setNewPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (newPassword !== confirm) {
      setError('Las contraseñas no coinciden.')
      return
    }

    if (newPassword.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.')
      return
    }

    try {
      const data = await activateMutation.mutateAsync({ token: token!, new_password: newPassword })
      setTokens(data.access, data.refresh, data.employee ?? { id: '', full_name: '', email: '' })
      setSuccess(true)
      setTimeout(() => navigate('/colaboradores', { replace: true }), 2000)
    } catch (err) {
      setError(extractErrorMessage(err, 'Error inesperado') || 'No se pudo guardar la contraseña.')
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center px-4">
        <div className="text-center">
          <div className="text-4xl mb-3">✓</div>
          <h2 className="text-xl font-semibold text-content">{restablecer ? 'Contraseña actualizada' : 'Cuenta activada'}</h2>
          <p className="text-sm text-content-muted mt-1">Redirigiendo al portal...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-content">{restablecer ? 'Contraseña nueva' : 'Activar la cuenta'}</h1>
          <p className="text-sm text-content-muted mt-1">
            {restablecer ? 'Portal del colaborador' : 'Crear la contraseña del portal del colaborador'}
          </p>
        </div>

        <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-6 shadow-sm">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div
                className="bg-danger/10 border border-danger/30 text-danger text-sm rounded-lg px-3 py-2"
                role="alert"
              >
                {error}
              </div>
            )}

            <div>
              <label htmlFor="new-password" className="block text-sm font-medium text-content mb-1">
                Nueva contraseña
              </label>
              <input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                className="w-full px-3 py-2 text-sm bg-input-bg border border-input-border rounded-lg
                           text-content placeholder:text-content-subtle
                           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
                placeholder="Mínimo 8 caracteres"
              />
            </div>

            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-content mb-1">
                Confirmar contraseña
              </label>
              <input
                id="confirm-password"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                autoComplete="new-password"
                className="w-full px-3 py-2 text-sm bg-input-bg border border-input-border rounded-lg
                           text-content placeholder:text-content-subtle
                           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
                placeholder="Repite la contraseña"
              />
            </div>

            <button
              type="submit"
              disabled={activateMutation.isPending}
              className="w-full py-2.5 px-4 bg-primary hover:bg-primary-hover text-on-primary font-medium
                         rounded-lg text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {activateMutation.isPending ? 'Guardando…' : restablecer ? 'Guardar la contraseña' : 'Activar la cuenta'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
